import { useLazyQuery } from '@apollo/client';
import { PickersDay, pickersDayClasses, PickersDayProps } from '@mui/lab';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import {
  add,
  addHours,
  endOfMonth,
  format,
  min as earliest,
  parseISO,
  startOfToday,
  startOfMonth,
} from 'date-fns';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '../app/hooks';
import {
  BOOKING_HORIZON_DAYS,
  bookingMaxDate,
} from '../util/bookingHorizon';
import { Appointment } from '../features/serviceLocation/serviceLocationSlice';
import {
  selectBestSlotAdjustmentLabel,
  selectPlanStartFeeIncluded,
  selectPotentialFranchiseId,
} from '../features/booking/bookingSlice';
import styles from '../sass/components/AppointmentDatePicker.module.scss';
import {
  GET_AVAILABILITY,
  GET_AVAILABILITY_LABELLED,
  GET_AVAILABILITY_LEGACY,
} from '../util/gql';
import { zoneAbbreviation, zoneDiffers, zoneSuffix } from '../util/slotTime';
import {
  formatCents,
  slotAdjustmentCents,
  slotTierLabel,
  SlotPriceTier,
} from '../util/bookingPricing';
import Datepicker from './Datepicker';
import {
  formatDate,
  formatTime,
  formatWholeDollars,
} from '../i18n/format';
import Select from './Select';
import TextInput from './TextInput';
import TimeList from './TimeList';
import {
  MINIMUM_HOURS_BEFORE_BOOKING_ALLOWED,
  FRANCHISE_MINIMUM_HOURS_BEFORE_BOOKING_ALLOWED,
} from '../constants/index.js';

const blueLightest = getComputedStyle(document.documentElement)
  .getPropertyValue('--color-primary-lightest').trim();
const blueDarkest = getComputedStyle(document.documentElement)
  .getPropertyValue('--blue-darkest').trim();
const blueWhite = getComputedStyle(document.documentElement)
  .getPropertyValue('--blue-white').trim();
const lowestPriceGreen = '#e6f2ed';
const lowestPriceGreenDark = '#219653';
const peakOrange = '#e87e01';

interface AppointmentDatePickerProps {
  onSelect: (date: Appointment) => void;
  value?: Appointment | undefined;
  timeNoLongerAvailable: boolean;
  franchiseId?: number | undefined;
  /** Show airline-style prices per day and per time slot. */
  showPrices?: boolean;
  /** Services subtotal (cents) the slot percentage is applied to. */
  eligibleSubtotalCents?: number;
  /**
   * Pre-tax order subtotal (cents, fees included) that prices are displayed
   * against, so every price on the page is the same "total before tax".
   * Defaults to eligibleSubtotalCents.
   */
  displaySubtotalCents?: number;
  /** Bump this to force availability (and prices) to reload. */
  refreshToken?: number;
  /** Pre-select the cheapest available time when prices load. */
  autoSelectBestDeal?: boolean;
  /** The basket is a subscription plan: prices shown are the first visit. */
  planBooking?: boolean;
  /**
   * Hide the per-day / per-time prices, the "lowest price" banner and tier
   * tags when every loaded time costs the same (Aperio plan bookings: the
   * same figure on every slot says nothing). Off by default, so every other
   * booking shows prices exactly as before.
   */
  hideUniformPrices?: boolean;
  /** Told whether the loaded times differ in price, once they are loaded. */
  onPricesVaryChange?: (vary: boolean) => void;
  /**
   * Called when a new day is picked on a phone before its time is: the
   * booking's time must not stay the old day's (QA #10: the page showed the
   * new date with the old slot's price, and Lock was still enabled).
   */
  onClear?: () => void;
  /**
   * Show the Date field as YYYY-MM-DD and explain which days can be picked
   * (the Aperio preset). Off by default: every other tenant's field and
   * copy are unchanged.
   */
  presetCopy?: boolean;
  /**
   * Shown on the Date field, which is marked invalid (QA 2026-09-25 #30):
   * the booking was attempted without a time.
   */
  errorMessage?: string;
}
export interface AppointmentMap {
  [day: string]: Appointment[]
}

/** How a calendar day should be highlighted, based on its cheapest slot. */
type DayPriceMood = 'lowest' | 'peak' | 'none';

export function getTimeValue(appointment: Appointment): string {
  const convertedDate = parseISO(appointment.date);

  // `date` is the service area's wall time; " PT" when the browser's
  // clock is in another zone (util/slotTime).
  return `${formatTime(convertedDate)}`
    + ` - ${formatTime(add(convertedDate, {
      hours: 1,
    }))}${zoneSuffix(appointment)}`;
}

type CustomPickerDayProps = PickersDayProps<Date> & {
  dayHasAvailability: boolean;
  priceMood?: DayPriceMood;
};

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'dayHasAvailability'
    && prop !== 'priceMood',
})<CustomPickerDayProps>(({ dayHasAvailability, priceMood }) => ({
  ...(dayHasAvailability && {
    backgroundColor: blueLightest,
    color: blueDarkest,
    '&:hover, &:focus': {
      backgroundColor: blueLightest,
    },
  }),
  ...(!dayHasAvailability && {
    backgroundColor: blueWhite,
    color: blueDarkest,
    '&:hover, &:focus': {
      backgroundColor: blueWhite,
    },
  }),
  ...(dayHasAvailability && priceMood === 'lowest' && {
    backgroundColor: lowestPriceGreen,
    color: lowestPriceGreenDark,
    fontWeight: 700,
    '&:hover, &:focus': {
      backgroundColor: lowestPriceGreen,
    },
  }),
  ...(dayHasAvailability && priceMood === 'peak' && {
    boxShadow: `inset 0 -3px 0 0 ${peakOrange}`,
  }),
})) as React.ComponentType<CustomPickerDayProps>;

export default function AppointmentDatePicker({
  onSelect,
  value,
  timeNoLongerAvailable,
  franchiseId,
  showPrices = false,
  eligibleSubtotalCents = 0,
  displaySubtotalCents = eligibleSubtotalCents,
  refreshToken = 0,
  autoSelectBestDeal = false,
  planBooking = false,
  hideUniformPrices = false,
  onPricesVaryChange,
  onClear,
  presetCopy = false,
  errorMessage,
}: AppointmentDatePickerProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 1080);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);
  const currentUser = useAppSelector((
    state,
  ) => state.auth.currentUser);
  const franchiseIdSelect = useAppSelector((state) => state.franchise.id);
  const potentialFranchiseId = useAppSelector(selectPotentialFranchiseId);
  const bestSlotAdjustmentLabel = useAppSelector(selectBestSlotAdjustmentLabel);
  // The plan's onboarding includes this first visit's mobile fee (Aperio).
  const feeIncluded = useAppSelector(selectPlanStartFeeIncluded);
  const autoSelectedFor = useRef<string | null>(null);

  const [
    day,
    setDay,
  ] = useState<Date | null>(value?.date ? parseISO(value.date) : new Date());
  const [
    appointment,
    setAppointment,
  ] = useState<Appointment | null | undefined>(value);

  // This booking's own postal code first: the shared one could be another
  // tab's (QA #28).
  const servicePostalCode = useAppSelector(
    (state) => state.serviceLocation.serviceAddress?.postalCode,
  );
  const storedPostalCode = useAppSelector(
    (state) => state.postalCode.postalCode,
  );
  const postalCode = servicePostalCode || storedPostalCode;
  // Slots carry startsAt / timeZone (the service area's zone). A server that
  // does not know those fields yet refuses the query: ask again without them
  // and keep doing so (the browser's own wall times, as before). The
  // switch changes getAvailability, which re-runs the first load below.
  // Slots also name the pricing rule that gave their price
  // (`priceAdjustmentLabel`), so a 7-day-out slot is never called "Book 14+
  // days ahead" (owner brief 2026-10-21); a server without those fields gets
  // the query without them.
  const [legacyAvailability, setLegacyAvailability] = useState(false);
  const [unlabelled, setUnlabelled] = useState(false);
  const [
    getLabelledAvailability, {
      data: labelledData,
      loading: labelledLoading,
    },
  ] = useLazyQuery(GET_AVAILABILITY_LABELLED, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      if (/Cannot query field "(appliedRules|priceAdjustmentLabel|startsAt|timeZone)"/
        .test(error.message)) {
        setUnlabelled(true);
      }
    },
  });
  const [
    getCurrentAvailability, {
      data: unlabelledData,
      loading: unlabelledLoading,
    },
  ] = useLazyQuery(GET_AVAILABILITY, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      if (/Cannot query field "(startsAt|timeZone)"/.test(error.message)) {
        setLegacyAvailability(true);
      }
    },
  });
  const currentData = unlabelled ? unlabelledData : labelledData;
  const currentLoading = unlabelled ? unlabelledLoading : labelledLoading;
  const [
    getLegacyAvailability, {
      data: legacyData,
      loading: legacyLoading,
    },
  ] = useLazyQuery(GET_AVAILABILITY_LEGACY, { fetchPolicy: 'network-only' });
  const data = legacyAvailability ? legacyData : currentData;
  const loading = legacyAvailability ? legacyLoading : currentLoading;
  const getAvailability = useCallback(
    (options: { variables: Record<string, unknown> }) => {
      if (legacyAvailability) return getLegacyAvailability(options);
      return unlabelled
        ? getCurrentAvailability(options)
        : getLabelledAvailability(options);
    },
    [legacyAvailability, unlabelled, getLegacyAvailability,
      getCurrentAvailability, getLabelledAvailability],
  );

  const isFranchiseRole: boolean = useMemo(
    () => currentUser.roles?.some(
      (role: string) => [
        'manager', 'technician', 'tester', 'sales',
      ].includes(role),
    ), [currentUser.roles],
  );

  const now = useMemo(() => {
    if (isFranchiseRole) {
      const today = new Date();
      return new Date(today.setDate(today.getDate() - 30));
    }
    return new Date();
  }, [isFranchiseRole]);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // A customer picks from today up to the booking horizon (QA #10); staff
  // keep their open calendar.
  const maxDate = useMemo(
    () => (isFranchiseRole ? undefined : bookingMaxDate()),
    [isFranchiseRole],
  );
  const capEnd = (end: Date): Date => (maxDate ? earliest([end, maxDate]) : end);

  useEffect(() => {
    const resize = () => {
      setIsMobile(window.innerWidth <= 1080);
    };

    window.addEventListener('resize', resize);

    return () => window.removeEventListener('resize', resize);
  }, []);

  const minumHours = isFranchiseRole
    ? FRANCHISE_MINIMUM_HOURS_BEFORE_BOOKING_ALLOWED
    : MINIMUM_HOURS_BEFORE_BOOKING_ALLOWED;

  useEffect(() => {
    getAvailability({
      variables: {
        start: addHours(now, minumHours).toJSON(),
        end: capEnd(endOfMonth(day || now)).toJSON(),
        postalCode: { code: postalCode },
        userTimezone,
        franchiseId: potentialFranchiseId || franchiseIdSelect || franchiseId,
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAvailability, now, postalCode, refreshToken]);

  const mappedAvailability: AppointmentMap = useMemo(() => ((data
    && data.getAvailability)
    ? data.getAvailability.reduce((
      map: AppointmentMap,
      current: Appointment,
    ) => {
      const newMap = map;
      const currentDay = format(parseISO(current.date), 'yyyyMMdd');
      if (newMap[currentDay]) {
        newMap[currentDay].push(current);
      } else {
        newMap[currentDay] = [current];
      }
      return newMap;
    }, {})
    : {}), [data]);

  /**
   * Cheapest adjustment (cents) per day, and whether any slot in the loaded
   * range is a "lowest price" slot. Used to colour the calendar.
   */
  const dayPriceMoods: { [day: string]: DayPriceMood } = useMemo(() => {
    if (!showPrices) return {};
    const moods: { [day: string]: DayPriceMood } = {};
    Object.entries(mappedAvailability).forEach(([dayKey, slots]) => {
      if (slots.some((slot) => slot.isLowestPrice)) {
        moods[dayKey] = 'lowest';
      } else if (slots.every(
        (slot) => slot.priceTier === SlotPriceTier.peak,
      )) {
        moods[dayKey] = 'peak';
      } else {
        moods[dayKey] = 'none';
      }
    });
    return moods;
  }, [mappedAvailability, showPrices]);

  const lowestPriceSummary = useMemo(() => {
    if (!showPrices || !data?.getAvailability?.length) return null;
    const slots: Appointment[] = data.getAvailability;
    // Slots come back in chronological order; the first lowest-price slot
    // that is still open is the best deal.
    const lowestSlots = slots.filter((slot) => slot.isLowestPrice);
    const lowest = lowestSlots.find((slot) => !slot.isBooked)
      || lowestSlots[0]
      || slots[0];
    const lowestCents = displaySubtotalCents
      + slotAdjustmentCents(lowest, eligibleSubtotalCents);
    const highestAdjustment = Math.max(
      ...slots.map((slot) => slotAdjustmentCents(slot, eligibleSubtotalCents)),
    );
    const lowestAdjustment = slotAdjustmentCents(lowest, eligibleSubtotalCents);
    return {
      lowest,
      lowestCents,
      lowestCount: lowestSlots.filter((slot) => !slot.isBooked).length,
      pricesVary: highestAdjustment !== lowestAdjustment,
    };
  }, [data, showPrices, eligibleSubtotalCents, displaySubtotalCents]);

  /** Cheapest price per calendar day, in cents, for the day cells. */
  const dayLowestCents: { [day: string]: number } = useMemo(() => {
    if (!showPrices) return {};
    const result: { [day: string]: number } = {};
    Object.entries(mappedAvailability).forEach(([dayKey, slots]) => {
      result[dayKey] = Math.min(
        ...slots.map(
          (slot) => displaySubtotalCents
            + slotAdjustmentCents(slot, eligibleSubtotalCents),
        ),
      );
    });
    return result;
  }, [mappedAvailability, showPrices, eligibleSubtotalCents, displaySubtotalCents]);

  /** Every loaded time costs the same, and the caller asked to say nothing. */
  const priceTextHidden = hideUniformPrices
    && !lowestPriceSummary?.pricesVary;

  useEffect(() => {
    if (!onPricesVaryChange || !lowestPriceSummary) return;
    onPricesVaryChange(lowestPriceSummary.pricesVary);
  }, [onPricesVaryChange, lowestPriceSummary]);

  // Airline-style default: land the customer on the best deal. Runs once per
  // availability load and only when nothing has been chosen yet.
  useEffect(() => {
    if (!autoSelectBestDeal || !showPrices || appointment) return;
    if (!lowestPriceSummary?.lowest || loading) return;
    // With no best deal to point at, land on the first time that is open.
    const lowest: Appointment = priceTextHidden
      ? ((data?.getAvailability || []) as Appointment[])
        .find((slot) => !slot.isBooked) || lowestPriceSummary.lowest
      : lowestPriceSummary.lowest;
    const key = `${lowest.routeId}_${lowest.date}`;
    if (autoSelectedFor.current === key) return;
    autoSelectedFor.current = key;
    // A full slot stays a request ("Request my booking"), never a promise.
    const appt: Appointment = { ...lowest, isBooked: !!lowest.isBooked };
    setDay(parseISO(appt.date));
    setAppointment(appt);
    onSelect(appt);
  }, [
    autoSelectBestDeal,
    showPrices,
    appointment,
    lowestPriceSummary,
    loading,
    onSelect,
    priceTextHidden,
    data,
  ]);

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
    }
  };

  // The Date and Time boxes are read-only and open the picker, from the
  // keyboard as well as the mouse: Enter, Space, or (Alt+) Arrow Down, as a
  // native date/select box does.
  const handleFieldKeyDown = (event: React.KeyboardEvent) => {
    if (anchorEl) return;
    if (event.key === 'Enter' || event.key === ' '
      || event.key === 'ArrowDown') {
      event.preventDefault();
      setAnchorEl(fieldsRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMonthChange = (date: Date) => {
    getAvailability({
      variables: {
        start: now >= date
          ? addHours(now, MINIMUM_HOURS_BEFORE_BOOKING_ALLOWED).toJSON()
          : startOfMonth(date).toJSON(),
        end: capEnd(endOfMonth(date)).toJSON(),
        postalCode: { code: postalCode },
        userTimezone,
        franchiseId: potentialFranchiseId || franchiseIdSelect || franchiseId,
      },
    });
    setDay(null);
  };

  const selectAppointment = (appt: Appointment) => {
    setAppointment(appt);
    onSelect(appt);
    handleClose();
  };

  const getIsDayAvailable = (date: Date): boolean => {
    const formattedDay = format(new Date(date), 'yyyyMMdd');
    let hasDate = false;
    if (mappedAvailability) {
      const avail = mappedAvailability;
      hasDate = Object.keys(avail).some((
        dateKey: string,
      ) => dateKey === formattedDay);
    }
    return hasDate;
  };

  const renderDay = (
    date: Date,
    selectedDates: Array<Date | null>,
    pickersDayProps: PickersDayProps<Date>,
  ) => {
    const dateIsAvailable = getIsDayAvailable(date);
    // A past day (and, for a customer, one beyond the horizon) cannot be
    // picked. It used to be a live button that jumped the panel to an
    // empty day (QA #10). Staff keep picking past days, as before.
    const outOfWindow = !isFranchiseRole
      && (date < startOfToday() || (!!maxDate && date > maxDate));
    if (date < startOfToday() || outOfWindow) {
      return (
        <PickersDay
          key={date.toString()}
          day={pickersDayProps.day}
          onDaySelect={pickersDayProps.onDaySelect}
          outsideCurrentMonth={pickersDayProps.outsideCurrentMonth}
          disabled={outOfWindow}
        />
      );
    }

    const dayKey = format(new Date(date), 'yyyyMMdd');
    const dayPrice = showPrices && !priceTextHidden && dateIsAvailable
      && dayLowestCents[dayKey] !== undefined
      ? formatWholeDollars(dayLowestCents[dayKey] / 100)
      : null;

    // The whole day, its lowest price and "Lowest price" when that is what
    // the colour means, for assistive tech: the button's own text is
    // "29 $131" and the tier was colour only (QA 2026-09-25 #30).
    const dayName = [
      formatDate(date, 'EEEE, MMMM d', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }),
      dayPrice ? t('schedule.day.from', { price: dayPrice }) : '',
      !priceTextHidden && dayPriceMoods[dayKey] === 'lowest'
        ? t('schedule.tier.lowest') : '',
      dateIsAvailable ? '' : t('schedule.day.none'),
    ].filter(Boolean).join(', ');

    return (
      <CustomPickersDay
        key={date.toString()}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...pickersDayProps}
        aria-label={dayName}
        disableMargin
        dayHasAvailability={dateIsAvailable}
        priceMood={priceTextHidden
          ? 'none' : dayPriceMoods[dayKey] || 'none'}
        disabled={!dateIsAvailable}
        sx={{
          ...(dayPrice && {
            flexDirection: 'column',
            lineHeight: 1,
            fontSize: '0.8rem',
            gap: '1px',
          }),
          [`&&.${pickersDayClasses.selected}`]: {
            backgroundColor: blueDarkest,
            color: '#F2F2F2',
            '&:hover, &:focus': {
              backgroundColor: blueDarkest,
            },
          },
          [`&&.${pickersDayClasses.disabled}`]: {
            backgroundColor: blueWhite,
            color: blueDarkest,
            '&:hover, &:focus': {
              backgroundColor: blueWhite,
            },
          },
        }}
      >
        {dayPrice ? (
          <>
            <span>{format(date, 'd')}</span>
            <span className={styles.dayPrice}>{dayPrice}</span>
          </>
        ) : undefined}
      </CustomPickersDay>
    );
  };

  const selectedDayTimes = mappedAvailability[
    format(day ? new Date(day) : new Date(), 'yyyyMMdd')
  ];

  // On mobile a full day is ~28 chips; keep the first few visible so the
  // payment and contact sections stay within reach, expanding on demand.
  const MOBILE_CHIP_LIMIT = 8;
  const [showAllTimes, setShowAllTimes] = useState(false);
  useEffect(() => {
    setShowAllTimes(false);
  }, [day]);
  const selectedChipIndex = (selectedDayTimes || []).findIndex(
    (time) => appointment?.date === time.date
      && appointment?.routeId === time.routeId,
  );
  const chipsExpanded = showAllTimes || selectedChipIndex >= MOBILE_CHIP_LIMIT;
  const visibleDayTimes = chipsExpanded
    ? selectedDayTimes
    : (selectedDayTimes || []).slice(0, MOBILE_CHIP_LIMIT);
  const hiddenTimeCount = (selectedDayTimes || []).length
    - (visibleDayTimes || []).length;

  const selectMobileTime = (event: ChangeEvent<HTMLSelectElement>) => {
    const [routeId, date] = event.currentTarget.value.split('_');
    const fullSlot = (selectedDayTimes || []).find(
      (time) => time.date === date && time.routeId === routeId,
    );
    const appt: Appointment = fullSlot
      ? { ...fullSlot, isBooked: !!fullSlot.isBooked }
      : { routeId, date, isBooked: false };
    setAppointment(appt);
    onSelect(appt);
  };

  const mobileTimeLabel = (time: Appointment): string => {
    if (!showPrices || priceTextHidden) return getTimeValue(time);
    const adjustment = slotAdjustmentCents(time, eligibleSubtotalCents);
    const tier = slotTierLabel(time);
    return `${getTimeValue(time)} · ${formatCents(
      displaySubtotalCents + adjustment,
    )}${tier ? ` (${tier})` : ''}`;
  };

  // Any slot of the loaded range names the service area's zone.
  const zoneSlot: Appointment | undefined = appointment?.timeZone
    ? appointment
    : (data?.getAvailability || []).find((slot: Appointment) => slot.timeZone);

  const isOpen = Boolean(anchorEl);
  const dayValue = appointment
    ? format(parseISO(appointment.date), presetCopy ? 'yyyy-MM-dd' : 'MM/dd/yy')
    : '';
  const timeValue = appointment
    ? getTimeValue(appointment)
    : '';
  const mobileTime = appointment ? `${appointment?.routeId}_`
    + `${appointment?.date}` : '';
  return (
    <div>
      {showPrices && lowestPriceSummary && !priceTextHidden && (
        <div className={styles.priceSummary}>
          {appointment?.isLowestPrice ? (
            <span>
              <span className={styles.priceSummaryLabel}>
                {t('schedule.deal.selected')}
              </span>
              {' '}
              {formatDate(
                parseISO(appointment.date),
                'EEEE, MMM d · h:mm aaa',
                {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                },
              )}
              {zoneSuffix(appointment)}
              {' · '}
              <span className={styles.priceSummaryValue}>
                {/* The chosen time's own price: after browsing another
                    month the "lowest" figure is that month's (QA #10). */}
                {formatCents(displaySubtotalCents
                  + slotAdjustmentCents(appointment, eligibleSubtotalCents))}
              </span>
              {'. '}
              <span className={styles.priceSummaryHint}>
                {t('schedule.deal.keepIt')}
              </span>
            </span>
          ) : (
            <span>
              <span className={styles.priceSummaryLabel}>
                {t('schedule.deal.lowest')}
              </span>
              {' '}
              <span className={styles.priceSummaryValue}>
                {formatCents(lowestPriceSummary.lowestCents)}
              </span>
              {t('schedule.deal.on')}
              {formatDate(
                parseISO(lowestPriceSummary.lowest.date),
                'EEE, MMM d · h:mm aaa',
                {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                },
              )}
              {zoneSuffix(lowestPriceSummary.lowest)}
              {'. '}
              <button
                type="button"
                className={styles.pickBestButton}
                onClick={() => selectAppointment({
                  ...lowestPriceSummary.lowest,
                  isBooked: !!lowestPriceSummary.lowest.isBooked,
                })}
              >
                {t('schedule.deal.pick')}
              </button>
            </span>
          )}
          {lowestPriceSummary.pricesVary && (
            <span className={styles.priceSummaryFacts}>
              {lowestPriceSummary.lowestCount > 0 && (
                <span>
                  {t('schedule.deal.left', {
                    count: lowestPriceSummary.lowestCount,
                  })}
                </span>
              )}
              {(lowestPriceSummary.lowest.priceAdjustmentLabel
                || bestSlotAdjustmentLabel) && (
                <span>
                  {' '}
                  {t('schedule.deal.rule', {
                    rule: lowestPriceSummary.lowest.priceAdjustmentLabel
                      || bestSlotAdjustmentLabel,
                  })}
                </span>
              )}
            </span>
          )}
          {planBooking && (
            <span className={styles.priceSummaryFacts}>
              {t(feeIncluded
                ? 'schedule.deal.planNoteFeeIncluded'
                : 'schedule.deal.planNote')}
            </span>
          )}
        </div>
      )}
      {isMobile && (
        <div className={cx(styles.wrapper, styles.mobile)}>
          <Datepicker
            onMonthChange={handleMonthChange}
            label={t('schedule.date')}
            value={appointment ? parseISO(appointment.date) : day}
            id="dayPicker"
            renderDay={renderDay}
            loading={loading}
            onChange={(event) => {
              setAppointment(null);
              setDay(event || null);
              onClear?.();
            }}
            maxDate={maxDate}
            mobile
          />
          {showPrices ? (
            <div className={styles.mobileTimes}>
              <span className="label">{t('schedule.time')}</span>
              {loading && (
                <p className={styles.mobileTimesHint}>
                  {t('schedule.loadingTimes')}
                </p>
              )}
              {!loading && !selectedDayTimes && (
                <p className={styles.mobileTimesHint}>
                  {t('schedule.pickDate')}
                </p>
              )}
              {!loading && selectedDayTimes && (
                <div
                  className={styles.mobileTimeGrid}
                  role="listbox"
                  aria-label={t('schedule.times')}
                >
                  {(visibleDayTimes || []).map((time) => {
                    const adjustment = slotAdjustmentCents(
                      time,
                      eligibleSubtotalCents,
                    );
                    // Preset: a full time reads "On request" and is drawn
                    // apart (apr-chip--request, public/aperio/theme.css);
                    // it looked like any open "Lowest price" time.
                    const asRequest = presetCopy && !!time.isBooked;
                    let tier = priceTextHidden ? null : slotTierLabel(time);
                    if (asRequest) tier = t('schedule.tier.request');
                    const isSelected = appointment?.date === time.date
                      && appointment?.routeId === time.routeId;
                    return (
                      <button
                        key={`${time.routeId}-${time.date}`}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={cx(styles.mobileTimeChip, {
                          [styles.selected]: isSelected,
                          [styles.booked]: time.isBooked,
                          [styles.lowest]: time.isLowestPrice
                            && !priceTextHidden,
                          [styles.peak]: !time.isLowestPrice
                            && !priceTextHidden
                            && time.priceTier === SlotPriceTier.peak,
                          'apr-chip--request': asRequest,
                        })}
                        onClick={() => {
                          // Keeps a full slot's flag: the step then says
                          // "Request my booking" and explains the request,
                          // as the desktop list does. Forcing false promised
                          // a firm booking the server then made a request.
                          const appt = { ...time, isBooked: !!time.isBooked };
                          setAppointment(appt);
                          onSelect(appt);
                        }}
                      >
                        <span className={styles.chipTime}>
                          {formatTime(parseISO(time.date))}
                          {zoneSuffix(time)}
                        </span>
                        {!priceTextHidden && (
                          <span className={styles.chipPrice}>
                            {formatCents(displaySubtotalCents + adjustment)}
                          </span>
                        )}
                        {tier && (
                          <span className={styles.chipTag}>{tier}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {!loading && selectedDayTimes && hiddenTimeCount > 0 && (
                <button
                  type="button"
                  className={styles.moreTimesButton}
                  onClick={() => setShowAllTimes(true)}
                >
                  {t('schedule.moreTimes', { count: hiddenTimeCount })}
                </button>
              )}
              {!loading && selectedDayTimes && showAllTimes
                && selectedDayTimes.length > MOBILE_CHIP_LIMIT && (
                <button
                  type="button"
                  className={styles.moreTimesButton}
                  onClick={() => setShowAllTimes(false)}
                >
                  {t('schedule.fewerTimes')}
                </button>
              )}
              {timeNoLongerAvailable && (
                <p className={styles.mobileTimesError}>
                  {t('schedule.timeGone')}
                </p>
              )}
            </div>
          ) : (
            <Select
              label={t('schedule.time')}
              id="appointment-time"
              value={loading ? 'loading' : mobileTime}
              placeholder={t('schedule.selectTime')}
              onChange={(
                event: React.ChangeEvent<HTMLSelectElement>,
              ) => selectMobileTime(event)}
              disabled={!selectedDayTimes}
              loading={loading}
              errorMessage={timeNoLongerAvailable
                ? t('schedule.timeGone') : ''}
            >
              {!selectedDayTimes && appointment && (
                <option
                  key={`${appointment.routeId}-${appointment.date}`}
                  disabled
                  value={`${appointment.routeId}_${appointment.date}`}
                >
                  {mobileTimeLabel(appointment)}
                </option>
              )}
              {selectedDayTimes
                && selectedDayTimes.map((
                  time,
                ) => (
                  <option
                    key={`${time.routeId}-${time.date}`}
                    onClick={() => setAppointment(time)}
                    value={`${time.routeId}_${time.date}`}
                  >
                    {mobileTimeLabel(time)}
                  </option>
                ))}
            </Select>
          )}
        </div>
      )}
      {/* Was a <button> wrapped around two text boxes: controls inside a
          control (axe nested-interactive), and boxes with no id, so their
          labels named nothing (QA 2026-09-25 #30). Now two labelled,
          read-only boxes that open the picker; the wrapper keeps the box the
          button's own UA padding and centring gave it, so nothing moves. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={handleClick}
        ref={fieldsRef}
        className={cx(styles.wrapper, styles.desktop, {
          'apr-date-fields': presetCopy,
        })}
        style={{
          paddingTop: 1,
          paddingRight: 6,
          textAlign: 'center',
          cursor: 'default',
        }}
      >
        <TextInput
          id="appointment-date"
          title={t('schedule.date')}
          onChange={() => undefined}
          onKeyDown={handleFieldKeyDown}
          value={dayValue}
          readOnly
          ariaHasPopup="dialog"
          ariaExpanded={isOpen}
          errorMessage={errorMessage}
          required
          // Preset hook: the full YYYY-MM-DD, never clipped (QA #49).
          className={cx(styles.dateInput, { 'apr-date-input': presetCopy })}
        />
        <TextInput
          id="appointment-time"
          title={t('schedule.timeShort')}
          onChange={() => undefined}
          onKeyDown={handleFieldKeyDown}
          value={timeValue}
          readOnly
          ariaHasPopup="dialog"
          ariaExpanded={isOpen}
          required
          className={cx(styles.timeInput, { 'apr-time-input': presetCopy })}
          errorMessage={timeNoLongerAvailable
            ? t('schedule.timeGone') : ''}
        />
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          PaperProps={{
            role: 'dialog',
            'aria-label': t('schedule.dateTimeDialog'),
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <div className={styles.dateAndTimeSection}>
            <Datepicker
              popout={false}
              onMonthChange={handleMonthChange}
              label={t('schedule.date')}
              value={day ? new Date(day) : null}
              id="dayPicker"
              renderDay={renderDay}
              loading={loading}
              onChange={(event) => setDay(event || null)}
              maxDate={maxDate}
            />
            {!!day && (
              <TimeList
                onSelectAppointment={selectAppointment}
                times={selectedDayTimes || []}
                selectedTime={appointment}
                showPrices={showPrices && !priceTextHidden}
                eligibleSubtotalCents={eligibleSubtotalCents}
                displaySubtotalCents={displaySubtotalCents}
              />
            )}
          </div>
        </Popover>
      </div>
      {presetCopy && !isFranchiseRole && (
        <p className="apr-date-note">
          {t('schedule.horizonNote', { days: BOOKING_HORIZON_DAYS })}
        </p>
      )}
      {zoneSlot && zoneDiffers(zoneSlot) && (
        // The browser is in another zone than the service address: every
        // time above is the address's own clock, and says so.
        <p className={presetCopy ? 'apr-date-note' : undefined}>
          {t('schedule.zoneNote', {
            zone: zoneAbbreviation(
              zoneSlot.timeZone as string,
              parseISO(zoneSlot.startsAt || zoneSlot.date),
            ),
          })}
        </p>
      )}
    </div>
  );
}
